import { Button, ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { useState } from "react";

import { useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import {
	type generateCartLinkForm_GenerateCartLinkMutation,
	type GenerateAbsoluteCartLinkInput,
} from "@relay/generateCartLinkForm_GenerateCartLinkMutation.graphql";
import { GENERATE_CART_LINK_MUTATION } from "./generate-cart-link-form.graphql";
import { type GenerateCartLinkFormState } from "./generate-cart-link-form.interface";
import { CartLink, OrderListWithoutControls } from "./generate-cart-link-form.styles";
import { ButtonVariant } from "../../../../components/button";
import { ValidatedField } from "../../../../components/ValidatedField";
import { SelectDiscountCodeFieldDialog } from "../../../../features/discount-codes/select-discount-code-field-dialog";
import { type ProductSelection } from "../../../../features/products/product-selection-form/product-selection-form.interface";
import { AddProductSelectionButton } from "../add-product-selection-button/add-product-selection-button.component";

export const GenerateCartLinkForm = () => {
	const { t } = useTranslation("common");
	const [cartLink, setCartLink] = useState<string | undefined>(undefined);

	const [generateCartLink, isGeneratingCartLink] =
		useMutation<generateCartLinkForm_GenerateCartLinkMutation>(GENERATE_CART_LINK_MUTATION);

	const handleOnClick = () => {
		if (cartLink) {
			void navigator.clipboard.writeText(cartLink);
			toast.success(t("generateCartLink.linkCopied"));
		}
	};

	const valuesToInput = (values: GenerateCartLinkFormState): GenerateAbsoluteCartLinkInput => ({
		selectedProducts: values.selectedProducts
			.filter((sp) => sp.product.id)
			.map((sp) => ({
				amount: sp.amount,
				productId: sp.product.id!,
			})),
		selectedDiscountCodes: values.selectedDiscountCodes,
	});

	const formik = useFormik<GenerateCartLinkFormState>({
		initialValues: {
			selectedProducts: [],
			selectedDiscountCodes: [],
		},
		onSubmit: (values, { setSubmitting }) => {
			generateCartLink({
				variables: {
					input: valuesToInput(values),
				},
				onCompleted: (response) => {
					setCartLink(response.Admin.Billing.generateAbsoluteCartLink?.link);
				},
			});
			setSubmitting(false);
		},
	});

	return (
		<div className="flex flex-column">
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<GenerateCartLinkFormState, ProductSelection[]>
					name={"selectedProducts"}
					label={t("order.products")}
					component={({ fieldValue, updateField }) => (
						<>
							<div className="p-buttonset mt-1 w-auto mb-2">
								<AddProductSelectionButton
									onSubmit={(values) => {
										updateField([...(fieldValue ?? []), { ...values }]);
									}}
								/>
							</div>
							<OrderListWithoutControls
								value={fieldValue}
								itemTemplate={(item) => {
									return (
										<div className="flex align-items-center">
											<div>{item.product?.title}</div>
											<div className="ml-2 mr-2">x</div>
											<div>{item.amount}</div>
										</div>
									);
								}}
							/>
						</>
					)}
					formikConfig={formik}
				/>
				<ValidatedField<GenerateCartLinkFormState, string[]>
					name={"selectedDiscountCodes"}
					label={t("order.discountCodes")}
					component={({ fieldValue, updateField, isValid, fieldName }) => (
						<>
							<div className="p-buttonset mt-1 w-auto mb-2">
								<SelectDiscountCodeFieldDialog
									fieldValue={undefined}
									updateField={(value) => {
										value?.code &&
											updateField([...(fieldValue ?? []), value?.code]);
									}}
									fieldName={fieldName}
									required={false}
									isValid={isValid}
									disabled={false}
								/>
							</div>
							<OrderListWithoutControls
								value={fieldValue}
								itemTemplate={(item) => {
									return (
										<div className="flex align-items-center justify-content-between">
											<div>{item}</div>
											<Button
												type={ButtonType.Button}
												icon={EpicIcons.TRASH}
												variant={ButtonVariant.Error}
												onClick={() => {
													updateField(
														fieldValue?.filter((v) => v !== item),
													);
												}}
											/>
										</div>
									);
								}}
							/>
						</>
					)}
					formikConfig={formik}
				/>
				<Button
					type={ButtonType.Submit}
					label={t("generateCartLink.generateLink")}
					disabled={isGeneratingCartLink}
					stretch
				/>
			</form>
			{cartLink && <CartLink onClick={handleOnClick}>{cartLink}</CartLink>}
		</div>
	);
};
